import type { FieldId } from './result'
import { formatDatum } from './dates'

export type FieldSource = 'AVGS' | 'Trägerbestätigung' | 'Stammdaten' | 'Organisation'

export type FieldError =
  | { kind: 'missing' }
  | { kind: 'malformed'; expected: 'date' | 'number' | 'email' | 'plz' | 'time' }
  | { kind: 'mismatch'; source: FieldSource; expected: string }
  | { kind: 'outOfRange'; source: FieldSource; von: string; bis: string }
  | { kind: 'maxEnde'; beginn: string; maxEnde: string; dauer: string }
  | { kind: 'oneOf' }

export type FieldErrors = ReadonlyMap<FieldId, FieldError>

export function formatFieldError(label: string, err: FieldError): string {
  switch (err.kind) {
    case 'missing':
      return `Feld „${label}" nicht gefunden`
    case 'malformed':
      switch (err.expected) {
        case 'number':
          return `Feld „${label}": keine gültige Zahl`
        case 'email':
          return `Feld „${label}": ungültige E-Mail-Adresse`
        case 'plz':
          return `Feld „${label}": PLZ muss aus 5 Ziffern bestehen`
        case 'time':
          return `Feld „${label}": ungültige Uhrzeit (HH:MM)`
        case 'date':
        default:
          return `Feld „${label}": ungültiges Datum`
      }
    case 'mismatch':
      return `Aktuell gespeichert (${err.source}): „${err.expected}"`
    case 'outOfRange':
      return `Außerhalb der Gültigkeit (${err.source}): ${err.von} – ${err.bis}`
    case 'maxEnde':
      return `Für Startdatum ${err.beginn} ist das maximale Enddatum ${err.maxEnde} (AVGS: ${err.dauer}).`
    case 'oneOf':
      return 'Mindestens eines dieser Felder ausfüllen'
  }
}

export const dateMismatch = (source: FieldSource, reference: unknown): FieldError => ({
  kind: 'mismatch',
  source,
  expected: formatDatum(reference),
})

export const outOfRange = (source: FieldSource, vonIso: unknown, bisIso: unknown): FieldError => ({
  kind: 'outOfRange',
  source,
  von: formatDatum(vonIso),
  bis: formatDatum(bisIso),
})

export const maxEndeError = (beginnIso: unknown, maxEndeIso: unknown, dauer: string): FieldError => ({
  kind: 'maxEnde',
  beginn: formatDatum(beginnIso),
  maxEnde: formatDatum(maxEndeIso),
  dauer,
})

export type FieldHintReason = { kind: 'gueltigkeit'; von: string; bis: string }

export function formatFieldHint(reason: FieldHintReason): string {
  switch (reason.kind) {
    case 'gueltigkeit':
      return `AVGS gültig: ${reason.von} – ${reason.bis}`
  }
}

export const gueltigkeitHint = (vonIso: unknown, bisIso: unknown): FieldHintReason => ({
  kind: 'gueltigkeit',
  von: formatDatum(vonIso),
  bis: formatDatum(bisIso),
})
